/**
 * Financeiro › Conta Corrente › Procedimentos.
 *
 * Guia estático (sem chamadas de API) do ponto de vista do EXTRATO: como ler as
 * linhas previstas, o que fazer quando um recebimento aparece "Não recebido" ao
 * lado do crédito real, e onde fica cada ferramenta. O detalhe da conciliação
 * de cartão continua no guia da Cielo (Recebíveis Cielo › Como conciliar); aqui
 * ficam os procedimentos que começam na tela de lançamentos.
 *
 * Os selos reproduzem os da tabela do extrato (FinancialTransactionsView): se um
 * rótulo mudar lá, mude aqui também.
 */
import React, { useEffect } from 'react';
import {
  BookOpen,
  CircleHelp,
  ClipboardList,
  Eye,
  ListChecks,
  MapPin,
} from 'lucide-react';
import { Badge, Btn, Callout, GoTo, Section, Situation, Steps } from './guideBlocks';
import { scrollToGuideSection } from './guideUtils';

interface StatementProceduresGuideProps {
  onOpenCielo: () => void;
  /** Só existe quando a conta tem integração com o extrato do Itaú e o usuário tem permissão. */
  onOpenItau?: () => void;
  /** Seção a mostrar ao abrir (ex.: o link "Como resolver" da linha vencida). */
  initialSection?: string;
}

/** Âncora do caso "previsto não recebido com crédito de centavos diferente". */
export const STATEMENT_GUIDE_OVERDUE_ANCHOR = 'proc-nao-recebido-centavos';

const TOC = [
  { id: 'proc-ler', label: '1. Como ler o extrato' },
  { id: 'proc-rotina', label: '2. Rotina diária' },
  { id: 'proc-situacoes', label: '3. O que fazer em cada situação' },
  { id: 'proc-onde', label: '4. Onde fica cada coisa' },
];

export const StatementProceduresGuide: React.FC<StatementProceduresGuideProps> = ({
  onOpenCielo,
  onOpenItau,
  initialSection,
}) => {
  useEffect(() => {
    if (initialSection) scrollToGuideSection(initialSection);
  }, [initialSection]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
          <BookOpen size={20} className="text-emerald-600" />
          Procedimentos da Conta Corrente
        </h2>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">
          Como ler o extrato e o que fazer quando algo não bate. A conciliação de cartão em detalhe está no guia{' '}
          <Btn>Recebíveis Cielo</Btn> → <Btn>Como conciliar</Btn>; aqui ficam os casos que você enxerga a partir dos
          lançamentos.
        </p>
        <nav className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {TOC.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => scrollToGuideSection(item.id)}
              className="text-left text-sm text-emerald-700 hover:underline"
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 1 */}
      <Section id="proc-ler" icon={<Eye size={18} />} title="1. Como ler o extrato">
        <p>O extrato mistura dois tipos de linha:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>
            <strong>Realizado</strong>: lançamentos de verdade (criados à mão, pela Conciliação Itaú ou pelo processo
            diário do extrato). É o que compõe <strong>Entradas</strong>, <strong>Saídas</strong>,{' '}
            <strong>Saldo Final</strong> e a coluna <strong>Saldo (realizado)</strong>, que deve bater com o banco.
          </li>
          <li>
            <Badge tone="sky">Previsto</Badge> “Previsto: Cielo Visa”, “N parcelas”: o que a Cielo deve pagar naquele dia
            e bandeira, calculado das vendas no cartão. Não é lançamento: entra só na coluna{' '}
            <strong>Saldo (com previsão)</strong> e nos cartões <strong>Previsto a receber no mês</strong> e{' '}
            <strong>Saldo final com previsão</strong>. Clique em “N parcelas” para ver vendas e pedidos.
          </li>
        </ul>
        <p>
          Quando o dia chega e o crédito da Cielo é conciliado, a linha prevista <strong>some</strong> e o crédito
          realizado passa a expandir as parcelas que pagou.
        </p>
        <p>
          <Badge tone="amber">Não recebido</Badge> marca uma previsão com data passada que ainda não foi conciliada. Ela
          fica fora do saldo com previsão e soma no cartão <strong>Vencido não recebido</strong>. Na maioria das vezes o
          dinheiro já entrou e só falta a baixa (seção 3).
        </p>
        <Callout>
          Use <strong>Agrupar por data</strong> (nos filtros) para ver, dia a dia, o previsto e o realizado lado a lado:
          é o jeito mais rápido de achar o crédito que corresponde a uma previsão.
        </Callout>
      </Section>

      {/* 2 */}
      <Section id="proc-rotina" icon={<ListChecks size={18} />} title="2. Rotina diária (depois das 06:00)">
        <Steps
          items={[
            <>
              O processo diário busca o extrato do Itaú, lança os créditos com correlação cadastrada (Cielo, PIX…) e
              baixa as previsões da Cielo que conferem.
            </>,
            <>
              Abra a Conta Corrente do mês e procure <Badge tone="amber">Não recebido</Badge> ou o cartão{' '}
              <strong>Vencido não recebido</strong> diferente de zero. Cada linha dessas é um caso da seção 3.
            </>,
            <>
              Se a conta tem integração com o Itaú, abra <Btn>Conciliação Itaú</Btn> e veja se sobrou evento{' '}
              <Badge tone="amber">Novo</Badge> sem lançamento.
            </>,
          ]}
        />
      </Section>

      {/* 3 */}
      <Section id="proc-situacoes" icon={<ClipboardList size={18} />} title="3. O que fazer em cada situação">
        <div id={STATEMENT_GUIDE_OVERDUE_ANCHOR} className="scroll-mt-4">
          <Situation
            badge={<Badge tone="amber">Não recebido</Badge>}
            title="Previsto não recebido, mas o crédito CIELO da mesma bandeira entrou no dia (diferença de centavos)"
            looks={
              <>
                Exemplo real, 21/09/2026: “Previsto: Cielo Visa · 10 parcelas · Não recebido” de R$ 7.903,56 e, no
                mesmo dia, “CIELO VISA CD1083442276” de R$ 7.903,52. Idem Mastercard: R$ 6.291,95 × R$ 6.291,92.
              </>
            }
            compare={
              <>
                Mesma data, mesma bandeira, diferença de poucos centavos. É arredondamento da taxa por parcela, não
                dinheiro faltando.
              </>
            }
            action={
              <Steps
                items={[
                  <>
                    Clique <Btn>Recebíveis Cielo</Btn> → <Btn>Conferir planilha da Cielo</Btn> e clique no dia no
                    calendário.
                  </>,
                  <>
                    Se o painel mostrar em vermelho <em>“Este dia está com o Detalhado desatualizado”</em>, exporte no
                    portal da Cielo o <strong>Detalhado</strong> a partir da data indicada até hoje (EC mostrado no
                    painel) e importe. A baixa automática fecha o dia na manhã seguinte.
                  </>,
                  <>
                    Para resolver na hora: na linha da bandeira (selo <Badge tone="amber">Confere · aguardando baixa</Badge>
                    ), clique <Btn>Baixar</Btn>, confira valor e conta e <Btn>Confirmar</Btn>. A mensagem verde deve dizer
                    que o crédito do extrato foi reaproveitado: nenhum lançamento novo é criado.
                  </>,
                  <>Volte aos lançamentos: a linha prevista some e o crédito passa a mostrar as parcelas.</>,
                ]}
              />
            }
            avoid={
              <>
                Não use <Btn>Não recebido</Btn> (o dinheiro entrou) e não crie lançamento de ajuste dos centavos: a
                diferença fica registrada no próprio depósito.
              </>
            }
          />
        </div>

        <Situation
          badge={<Badge tone="amber">Não recebido</Badge>}
          title="Previsto não recebido e nenhum crédito CIELO da bandeira no dia"
          looks="A previsão venceu e o extrato do dia não tem crédito da Cielo daquela bandeira."
          compare="O dia seguinte (a Cielo às vezes paga 1 a 3 dias depois) e se o extrato do dia já foi carregado."
          action={
            <>
              Espere um dia útil. Se continuar, importe o Detalhado da Cielo cobrindo a data: se a venda foi paga em
              outro dia, a previsão é realocada sozinha para a data real. Se o arquivo não trouxer a venda, siga o caso
              “Só na previsão” do guia da Cielo.
            </>
          }
          avoid="Não marque Não recebido sem confirmar no portal da Cielo que a venda foi cancelada ou estornada."
        />

        <Situation
          badge={<Badge tone="slate">Realizado</Badge>}
          title="Crédito CIELO sem nenhuma previsão no dia"
          looks="Um crédito “CIELO …” entrou e não há linha prevista correspondente."
          action={
            <>
              Venda que a agenda não conhecia (antiga, ou de outra origem). Importe o Detalhado do período em{' '}
              <Btn>Recebíveis Cielo</Btn>: o dia aparece como “Só no arquivo” e o crédito ganha a identificação das
              vendas. Nenhum lançamento novo é necessário.
            </>
          }
        />

        <Situation
          badge={<Badge tone="red">Duplicado</Badge>}
          title="Crédito da Cielo lançado duas vezes"
          looks={
            <>
              No mesmo dia há “CIELO VISA CD…” (do extrato, processo diário) e outro “CIELO VISA” lançado à mão.
            </>
          }
          compare="Abra a Conciliação Itaú do dia: só existe UM crédito no banco para cada bandeira."
          action={
            <>
              Exclua o lançamento manual (o que não tem “CD” + número do EC na descrição), depois de confirmar que o valor
              é o mesmo do extrato. Se os valores forem diferentes, o manual pode ser outro recebimento: confira antes.
            </>
          }
          avoid="Não exclua o lançamento criado pelo processo do extrato: ele é o que está vinculado às parcelas."
        />

        <Situation
          badge={<Badge tone="amber">Novo</Badge>}
          title="Evento do Itaú ainda sem lançamento"
          looks="Na Conciliação Itaú, um evento aparece como Novo."
          action={
            <>
              Marque a linha, confira a categoria sugerida e clique <Btn>Criar lançamentos (N)</Btn>. Use{' '}
              <Btn>Já conciliado</Btn> só se o lançamento já existe na conta com o mesmo valor e data.
            </>
          }
        />
      </Section>

      {/* 4 */}
      <Section id="proc-onde" icon={<MapPin size={18} />} title="4. Onde fica cada coisa">
        <ul className="list-disc ml-5 space-y-1">
          <li>
            <Btn>Recebíveis Cielo</Btn>: importar o Detalhado da Cielo, calendário de conferência, baixar ou marcar não
            recebido. Tem o guia completo em <Btn>Como conciliar</Btn>.
          </li>
          <li>
            <Btn>Conciliação Itaú</Btn>: o extrato direto do banco, evento a evento (só em conta com integração ativa).
          </li>
          <li>
            <Btn>Configuração</Btn> → <Btn>Operações</Btn> → <Btn>Execução de Processos</Btn>: o resultado do processo
            diário do extrato, com os avisos de recebível pendente.
          </li>
        </ul>
        <div className="flex flex-wrap gap-2">
          <GoTo onClick={onOpenCielo}>Abrir Recebíveis Cielo</GoTo>
          <GoTo onClick={onOpenItau}>Abrir Conciliação Itaú</GoTo>
        </div>
        <p className="flex items-start gap-2 text-xs text-slate-500">
          <CircleHelp size={14} className="mt-0.5 flex-shrink-0" />
          Dúvida que este guia não cobre: anote data, valor, descrição da linha e o que aparece na conferência da
          Cielo, e leve ao responsável financeiro.
        </p>
      </Section>
    </div>
  );
};
